<script lang="ts" setup>
import { computed } from 'vue';
import FFTextArea from '@/components/Shared/Input/TextInputs/TextArea.vue';

const props = defineProps<{
  title: string;
  placeholder: string;
  aiPlaceholder?: string;
  modelValue?: string | null;
  aiInsight?: string | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'update:aiInsight': [value: string | null];
}>();

const titleText = computed(() => props.title);
const placeholderText = computed(() => props.placeholder);
const aiPlaceholderText = computed(
  () => props.aiPlaceholder || 'AI-generated insights will appear here...',
);

const handleUserInput = (value: string) => {
  emit('update:modelValue', value || '');
};

const handleAiInput = (value: string) => {
  emit('update:aiInsight', value || null);
};
</script>

<template>
  <div>
    <span class="font-sans text-sm font-bold leading-[18px] text-shade-880">
      {{ titleText }}
    </span>

    <div
      class="relative mt-2 rounded-md border border-tint-80 focus-within:ring-2 focus-within:ring-highlight-500 focus-within:ring-offset-2"
    >
      <!-- User input area -->
      <FFTextArea
        :model-value="props.modelValue"
        :placeholder="placeholderText"
        class="user-input-area"
        @update:model-value="handleUserInput"
        :rows="3"
      />

      <template v-if="props.aiInsight">
        <!-- Styled divider - not editable -->
        <div class="flex items-center bg-white px-3 py-2">
          <hr class="enhanced-insights-divider flex-1" />
          <div class="flex items-center gap-2 px-3">
            <img
              src="@/assets/svg/jobs/copilot-lightning-purple.svg"
              alt="Copilot Lightning"
              class="h-4 w-4"
            />
            <span class="text-xs font-bold">
              <span class="enhanced-insights-divider-text">Enhanced Evaluation Insights</span></span
            >
          </div>
          <hr class="enhanced-insights-divider flex-1" />
        </div>

        <!-- AI content area -->
        <FFTextArea
          :model-value="props.aiInsight"
          :placeholder="aiPlaceholderText"
          class="ai-input-area"
          @update:model-value="handleAiInput"
          :rows="3"
        />
      </template>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
:deep(.user-input-area .v-field__input) {
  @apply min-h-14 rounded-b-none border-0 bg-white shadow-none;
  @apply focus:ring-0 focus:ring-offset-0;
}

:deep(.ai-input-area .v-field__input) {
  @apply rounded-t-none border-0 text-shade-900 shadow-none;
  background: linear-gradient(to top, rgba(147, 51, 234, 0.1), transparent);
  @apply focus:ring-0 focus:ring-offset-0;
}

:deep(.user-input-area .v-field),
:deep(.ai-input-area .v-field) {
  @apply border-0 shadow-none;
}

:deep(.user-input-area .v-field__outline),
:deep(.ai-input-area .v-field__outline) {
  @apply border-0;
}

:deep(.v-input__details) {
  @apply hidden;
}

.enhanced-insights-divider-text {
  background: var(--Linear, linear-gradient(270deg, #ae35f7 -15.75%, #5f30d1 71.06%));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.enhanced-insights-divider {
  @apply border-t border-dashed border-copilot-500/40;
}
</style>
